import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LOCALS } from '@constants/config';
import { STATUS_CODE } from '@constants/api';
import { SWITCH_ROUTES } from '@constants/routes';
import { getSeats, getSeatHis, getCasinoPlay, exitSeat } from '@api/casino';
import { useGlobalProps } from '@hooks/useGlobalProps';

const SEATS_PER_PAGE = 20;

/**
 * CasinoSeatModal component
 * Modal for selecting casino seats
 *
 * @param {Object} props
 * @param {boolean} props.show - Whether modal is visible
 * @param {Function} props.onClose - Close handler
 * @param {Array} props.game - Casino game data from casino_list
 */
export default function CasinoSeatModal({ show, onClose, game }) {
    const { t, i18n } = useTranslation();
    const tCommon = t('common', { returnObjects: true });
    const navigate = useNavigate();
    const { user, balance } = useGlobalProps();

    const [loading, setLoading] = useState(false);
    const [seats, setSeats] = useState([]);
    const [totalSeats, setTotalSeats] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [historyPopup, setHistoryPopup] = useState(null); // { cacc, data }
    const [historyLoading, setHistoryLoading] = useState(false);
    const [enteringGame, setEnteringGame] = useState(false);
    const [exitingGame, setExitingGame] = useState(false);
    // 被點擊的已佔用座位號碼，用於顯示列內提示（2 秒後自動清除）
    const [occupiedSeatNo, setOccupiedSeatNo] = useState(null);

    const gameIndex = game?.[0]; // A, B, C...
    const nameEN = game?.[2];
    const nameCN = game?.[3];
    const isChinese = i18n.language === LOCALS.cn;
    const displayName = isChinese ? nameCN : nameEN;

    const totalPages = Math.ceil(totalSeats / SEATS_PER_PAGE);

    // Fetch seats when modal opens or page changes
    const fetchSeats = useCallback(async () => {
        if (!show || !gameIndex || !user?.ukey) return;

        setLoading(true);
        try {
            const ipg = `${gameIndex}${currentPage}`;
            const res = await getSeats({
                pgc: SEATS_PER_PAGE,
                ipg,
                ukey: user.ukey,
            });

            if (res.rtn === STATUS_CODE.SUCCESS) {
                setSeats(res.seat_list || []);
                setTotalSeats(res.ttl_seats || 0);
            } else {
                console.error('getSeats error:', res.msg);
                setSeats([]);
            }
        } catch (error) {
            console.error('getSeats error:', error);
            setSeats([]);
        } finally {
            setLoading(false);
        }
    }, [show, gameIndex, currentPage, user?.ukey]);

    useEffect(() => {
        if (show) {
            setCurrentPage(1);
            setHistoryPopup(null);
            setOccupiedSeatNo(null);
        }
    }, [show, gameIndex]);

    useEffect(() => {
        fetchSeats();
    }, [fetchSeats]);

    // 換頁時清除提示（座位號碼會重複，避免殘留在錯誤的列）
    useEffect(() => {
        setOccupiedSeatNo(null);
    }, [currentPage]);

    // 提示 2 秒後自動消失；連續點擊時 cleanup 會取消舊計時器
    useEffect(() => {
        if (occupiedSeatNo === null) return;
        const timer = setTimeout(() => setOccupiedSeatNo(null), 2000);
        return () => clearTimeout(timer);
    }, [occupiedSeatNo]);

    // Handle history click
    const handleHistoryClick = async (cacc, e) => {
        e.stopPropagation();
        if (!cacc || !user?.ukey) return;

        setHistoryLoading(true);
        setHistoryPopup({ cacc, data: null });

        try {
            const res = await getSeatHis({ cacc, ukey: user.ukey });
            if (res.rtn === STATUS_CODE.SUCCESS) {
                setHistoryPopup({ cacc, data: res.seat_his });
            } else {
                console.error('getSeatHis error:', res.msg);
                setHistoryPopup(null);
            }
        } catch (error) {
            console.error('getSeatHis error:', error);
            setHistoryPopup(null);
        } finally {
            setHistoryLoading(false);
        }
    };

    // Handle seat click (enter game)
    const handleSeatClick = async (seat) => {
        const cacc = seat[1]; // casino account
        const seatNo = seat[0];
        const seatUsername = seat[2];
        if (!cacc || !user?.ukey || enteringGame) return;

        // 已被其他帳號佔用的座位：直接顯示提示，不呼叫 API
        const isEmpty = !seatUsername;
        const isMyself = seatUsername === user?.acc;
        if (!isEmpty && !isMyself) {
            setOccupiedSeatNo(seatNo);
            return;
        }

        setEnteringGame(true);
        try {
            const res = await getCasinoPlay({ cacc, ukey: user.ukey });
            if (res.rtn === STATUS_CODE.SUCCESS && res.play_url) {
                onClose();
                navigate(SWITCH_ROUTES.GAME, {
                    state: {
                        gameUrl: res.play_url,
                        isCasino: true,
                        casinoGame: game, // Pass game data for reopening modal
                    },
                });
            } else {
                console.error('getCasinoPlay error:', res.msg);
                alert(res.msg || 'Failed to enter game');
            }
        } catch (error) {
            console.error('getCasinoPlay error:', error);
            alert('Failed to enter game');
        } finally {
            setEnteringGame(false);
        }
    };

    // Handle exit seat
    const handleExitSeat = async (e) => {
        e.stopPropagation();
        if (!user?.ukey || exitingGame) return;

        setExitingGame(true);
        try {
            const res = await exitSeat({ ukey: user.ukey });
            if (res.rtn === STATUS_CODE.SUCCESS) {
                // Close modal after successful exit
                onClose();
            } else {
                console.error('exitSeat error:', res.msg);
                alert(res.msg || 'Failed to exit seat');
            }
        } catch (error) {
            console.error('exitSeat error:', error);
            alert('Failed to exit seat');
        } finally {
            setExitingGame(false);
        }
    };

    // Pagination handlers
    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    if (!show) return null;

    const handleOverlayClick = (e) => {
        e.stopPropagation();
        onClose();
    };

    // User info for display
    const username = user?.acc || '';
    const displayBalance = balance?.pts ?? 0;

    const modalContent = (
        <ModalOverlay onClick={handleOverlayClick}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <HeaderLeft>
                        <h3>{displayName}</h3>
                        <UserInfo>
                            <span className="balance-label">Balance:</span>
                            <span className="balance">{displayBalance} Cr.</span>
                            <span className="username">{username}</span>
                        </UserInfo>
                    </HeaderLeft>
                    <CloseIcon onClick={onClose} />
                </ModalHeader>

                <ModalBody>
                    {loading ? (
                        <LoadingWrapper>
                            <img src="/images/shared/loading.gif" alt="loading" />
                        </LoadingWrapper>
                    ) : (
                        <>
                            <SeatTable>
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>{tCommon['casino@col_no']}</th>
                                        <th>{tCommon['casino@col_user']}</th>
                                        <th>{tCommon['casino@col_tracker']}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {seats.map((seat, idx) => {
                                        const [seatNo, cacc, seatUsername, history] = seat;
                                        const isEmpty = !seatUsername;
                                        const isMyself = seatUsername === username;
                                        return (
                                            <React.Fragment key={cacc || idx}>
                                                <SeatRow
                                                    $isEmpty={isEmpty}
                                                    $isMyself={isMyself}
                                                    onClick={() => handleSeatClick(seat)}
                                                >
                                                    <td className="action-cell">
                                                        {isMyself && (
                                                            <ExitButton
                                                                onClick={handleExitSeat}
                                                                disabled={exitingGame}
                                                                title="Exit Seat"
                                                            >
                                                                {exitingGame ? '...' : <ExitIcon />}
                                                            </ExitButton>
                                                        )}
                                                    </td>
                                                    <td>{seatNo}</td>
                                                    <td>{seatUsername || '-'}</td>
                                                    <td>
                                                        {history ? (
                                                            <HistoryLink
                                                                onClick={(e) => handleHistoryClick(cacc, e)}
                                                            >
                                                                {history}
                                                            </HistoryLink>
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </td>
                                                </SeatRow>
                                                {occupiedSeatNo === seatNo && (
                                                    <OccupiedRow>
                                                        <td colSpan={4}>
                                                            {tCommon['casino@seat_occupied']}
                                                        </td>
                                                    </OccupiedRow>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </SeatTable>

                            {totalPages > 1 && (
                                <Pagination>
                                    <PageButton
                                        onClick={() => goToPage(1)}
                                        disabled={currentPage === 1}
                                    >
                                        &laquo;
                                    </PageButton>
                                    <PageButton
                                        onClick={() => goToPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        &lsaquo;
                                    </PageButton>
                                    <PageInfo>
                                        {currentPage} / {totalPages}
                                    </PageInfo>
                                    <PageButton
                                        onClick={() => goToPage(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    >
                                        &rsaquo;
                                    </PageButton>
                                    <PageButton
                                        onClick={() => goToPage(totalPages)}
                                        disabled={currentPage === totalPages}
                                    >
                                        &raquo;
                                    </PageButton>
                                </Pagination>
                            )}
                        </>
                    )}
                </ModalBody>

                {/* History Popup */}
                {historyPopup && (
                    <HistoryPopup onClick={() => setHistoryPopup(null)}>
                        <HistoryContent onClick={(e) => e.stopPropagation()}>
                            <HistoryHeader>
                                <span>{tCommon['casino@history_title']}</span>
                                <CloseIcon onClick={() => setHistoryPopup(null)} />
                            </HistoryHeader>
                            {historyLoading ? (
                                <LoadingWrapper>
                                    <img src="/images/shared/loading.gif" alt="loading" />
                                </LoadingWrapper>
                            ) : historyPopup.data?.length > 0 ? (
                                <HistoryTable>
                                    <thead>
                                        <tr>
                                            {historyPopup.data[0].map((cell, i) => (
                                                <th key={i}>{cell}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historyPopup.data.slice(1).map((row, idx) => (
                                            <tr key={idx}>
                                                {row.map((cell, i) => (
                                                    <td key={i}>{cell}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </HistoryTable>
                            ) : (
                                <p>No data</p>
                            )}
                        </HistoryContent>
                    </HistoryPopup>
                )}

                {enteringGame && (
                    <LoadingOverlay>
                        <img src="/images/shared/loading.gif" alt="loading" />
                    </LoadingOverlay>
                )}
            </ModalContent>
        </ModalOverlay>
    );

    // Use portal to render at document body level, avoiding z-index issues with sidebar
    return createPortal(modalContent, document.body);
}

// Styled Components
const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    position: relative;
    background: #fff;
    border-radius: 8px;
    width: 90%;
    max-width: 500px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border-bottom: 1px solid #eee;

    h3 {
        margin: 0;
        font-size: 18px;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const UserInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #666;

    .balance-label {
        color: #666;
    }

    .balance {
        font-weight: 600;
        color: ${({ theme }) => theme.primary || '#1890ff'};
        margin-right: 8px;
    }

    .username {
        color: #333;
    }
`;

const CloseIcon = styled.div`
    cursor: pointer;
    mask-image: url('/images/shared/close.svg');
    mask-repeat: no-repeat;
    mask-position: center;
    mask-size: contain;
    min-width: 18px;
    min-height: 18px;
    background: black;

    &:hover {
        background: #333;
    }
`;

const ModalBody = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 16px;
`;

const LoadingWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;

    img {
        width: 40px;
        height: 40px;
    }
`;

const SeatTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th,
    td {
        padding: 10px 8px;
        text-align: left;
        border-bottom: 1px solid #eee;
    }

    th {
        background: #f5f5f5;
        font-weight: 600;
        font-size: 14px;
    }

    td {
        font-size: 13px;
    }

    /* No 欄：座位號可能是 1-1 / 123-12，需保證單行並縮到內容寬度 */
    th:nth-child(2),
    td:nth-child(2) {
        white-space: nowrap;
        width: 1%;
    }
`;

const SeatRow = styled.tr`
    cursor: pointer;
    background: ${({ $isEmpty, $isMyself }) =>
        $isMyself ? '#fff3e0' : ($isEmpty ? '#f0fff0' : '#fff')};

    &:hover {
        background: ${({ $isEmpty, $isMyself }) =>
            $isMyself ? '#ffe0b2' : ($isEmpty ? '#e0ffe0' : '#f9f9f9')};
    }

    .action-cell {
        width: 60px;
        text-align: center;
    }
`;

const OccupiedRow = styled.tr`
    background: #fff1f0;

    td {
        padding: 8px;
        text-align: center;
        color: #cf1322;
        font-size: 13px;
        font-weight: 600;
        border-bottom: 1px solid #eee;
    }
`;

const ExitButton = styled.button`
    background: #ff4d4f;
    color: #fff;
    border: none;
    border-radius: 6px;
    width: 48px;
    height: 48px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover:not(:disabled) {
        background: #ff7875;
    }

    &:disabled {
        background: #ccc;
        cursor: not-allowed;
    }
`;

// Exit icon - door with arrow pointing out
const ExitIcon = () => (
    <svg width="36" height="36" viewBox="0 0 20 20" fill="currentColor">
        <path d="M13 3v2h2v10h-2v2h4V3h-4zm0 8V9H5.4l4.3-4.3-1.4-1.4L1.6 10l6.7 6.7 1.4-1.4L5.4 11H13z"/>
    </svg>
);

const HistoryLink = styled.span`
    color: #1890ff;
    cursor: pointer;
    text-decoration: underline;

    &:hover {
        color: #40a9ff;
    }
`;

const Pagination = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px 0 0;
`;

const PageButton = styled.button`
    background: ${({ theme }) => theme.primary || '#1890ff'};
    color: #fff;
    border: none;
    border-radius: 4px;
    padding: 8px 12px;
    cursor: pointer;
    font-size: 14px;

    &:disabled {
        background: #ccc;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        opacity: 0.9;
    }
`;

const PageInfo = styled.span`
    padding: 0 12px;
    font-size: 14px;
`;

const HistoryPopup = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
`;

const HistoryContent = styled.div`
    background: #fff;
    border-radius: 8px;
    padding: 16px;
    min-width: 250px;
    max-width: 90%;
`;

const HistoryHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;

    span {
        font-weight: 600;
    }
`;

const HistoryTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th,
    td {
        padding: 8px;
        border-bottom: 1px solid #eee;
        font-size: 13px;
    }

    th {
        font-weight: 600;
        background: #f5f5f5;
        text-align: left;
    }
`;

const LoadingOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;

    img {
        width: 40px;
        height: 40px;
    }
`;
